/**
 * FindMyDeviceTab — Palcare-only tab for remote device location & alarm.
 *
 * Two sections:
 *   1. Remote Control Panel — Find Device / Alarm Device buttons
 *   2. Device Location — Leaflet map with Google roadmap layer
 *
 * Flow:
 *   - User clicks Find or Alarm → sends remote command via parent handler
 *   - Begins polling every 5 seconds for device location (getpalcarelastlocation)
 *   - When location data returns, shows marker on map and centers on it
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import MapController from '../../../../components/component-assets/MapController.jsx';
import { convertIsoUTCToLocalized } from '../../../../libs.jsx';

const FindMyDeviceTab = ({
  serialNum,
  campusLat,
  campusLong,
  onFindDevice,
  onAlarmDevice,
  onPollLocation,
  tzOffset,
}) => {
  // Remote command states
  const [isFinding, setIsFinding] = useState(false);
  const [isAlarming, setIsAlarming] = useState(false);
  const [findDate, setFindDate] = useState('');
  const [statusText, setStatusText] = useState('');

  // Map state
  const [foundLocation, setFoundLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([campusLat || 0, campusLong || 0]);

  // Polling ref
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Start polling for location
  const startPolling = useCallback((lastDate) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollCountRef.current = 0;

    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      setStatusText(`Searching for device... (${pollCountRef.current * 5}s)`);

      try {
        const data = await onPollLocation(lastDate);
        if (data && data.length > 0) {
          const loc = data[0];
          setFoundLocation(loc);
          setMapCenter([loc.latitude, loc.longitude]);
          setIsFinding(false);
          setIsAlarming(false);
          setFindDate('');
          setStatusText('Device located!');

          // Stop polling
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (err) {
        console.error('[FindMyDeviceTab] Poll error:', err);
      }

      // Stop after ~2 minutes (24 polls × 5s)
      if (pollCountRef.current >= 24) {
        setIsFinding(false);
        setIsAlarming(false);
        setStatusText('Location request timed out. Try again.');
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }, 5000);
  }, [onPollLocation]);

  // Handle Find Device
  const handleFind = useCallback(async () => {
    if (isFinding || isAlarming) return;
    setIsFinding(true);
    setFoundLocation(null);
    setStatusText('Sending find command...');

    const timestamp = await onFindDevice();
    if (timestamp) {
      setFindDate(timestamp);
      startPolling(timestamp);
    } else {
      setIsFinding(false);
      setStatusText('Failed to send find command.');
    }
  }, [isFinding, isAlarming, onFindDevice, startPolling]);

  // Handle Alarm Device
  const handleAlarm = useCallback(async () => {
    if (isFinding || isAlarming) return;
    setIsAlarming(true);
    setFoundLocation(null);
    setStatusText('Sending alarm command...');

    const timestamp = await onAlarmDevice();
    if (timestamp) {
      setFindDate(timestamp);
      startPolling(timestamp);
    } else {
      setIsAlarming(false);
      setStatusText('Failed to send alarm command.');
    }
  }, [isFinding, isAlarming, onAlarmDevice, startPolling]);

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Remote Control Panel</h3>
      </div>
      <div className="pp-section__body pp-section__body--flush">
        <div className="ip-find-grid">
          {/* Controls */}
          <div className="ip-find-controls">
            <div className="ip-find-panel">
              <span className="ip-find-panel__title">Remote Commands</span>
              <button
                className={`ip-find-btn ${isFinding ? 'ip-find-btn--active' : 'ip-find-btn--find'}`}
                onClick={handleFind}
                disabled={isFinding || isAlarming}
              >
                {isFinding ? 'Locating...' : 'Find Device'}
              </button>

              <button
                className={`ip-find-btn ${isAlarming ? 'ip-find-btn--active' : 'ip-find-btn--alarm'}`}
                onClick={handleAlarm}
                disabled={isFinding || isAlarming}
              >
                {isAlarming ? 'Locating...' : 'Alarm Device'}
              </button>

              <span className="ip-find-status">{statusText}</span>
            </div>
          </div>

          {/* Map */}
          <div className="ip-map-container">
            <MapContainer
              zoom={19}
              scrollWheelZoom={true}
              center={mapCenter}
              style={{ height: '100%', width: '100%' }}
            >
              <ReactLeafletGoogleLayer apiKey="AIzaSyDWuEm0U-KbxGBwYlNyc3eiKM1lbWcocis" type="roadmap" />
              <MapController lat={mapCenter[0]} lng={mapCenter[1]} />
              {foundLocation && (
                <Marker position={[foundLocation.latitude, foundLocation.longitude]}>
                  <Popup>
                    <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                      <strong>Found at:</strong> {convertIsoUTCToLocalized(foundLocation.create_time)}<br />
                      <strong>Lat:</strong> {foundLocation.latitude}<br />
                      <strong>Long:</strong> {foundLocation.longitude}
                    </div>
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindMyDeviceTab;
